import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { filterCustomers } from "../lib/customerSearch";
import { filterServices, filterPackages } from "../lib/catalogSearch";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useBranch } from "@/context/BranchContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Calendar as CalendarIcon, ChevronLeft, ChevronRight, List, Grid3x3, X, Minus, Clock, Phone, User, CheckCircle2, XCircle, UserCheck, StickyNote, MoreVertical, Building2, UserPlus, Mail, LogIn, Receipt, Scissors, AlertTriangle, Search } from "lucide-react";
import { localizeError } from "@/lib/errors";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { resolveNewCustomerPhoneDuplicate } from "@/lib/customerPhoneDuplicate";
import PickPackageModal from "@/components/PickPackageModal";
import BookingQuickActions from "@/components/BookingQuickActions";
import BookingsDeskView from "@/components/BookingsDeskView";
import { canViewBookingsOwnerBoard } from "@/lib/roles";
import { selectedServiceLabel } from "@/lib/packageLines";
import {
  bookingSessionState as deriveBookingSessionState,
  liveSessionMinutes as deriveLiveSessionMinutes,
  isBookingStale,
  todayISO as bookingTodayISO,
} from "@/lib/bookingSession";

const DEFAULT_SLOTS = ["10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00"];
// Calendar grid config: start 9 AM to 10 PM, 30-min increments
const DAY_START_HOUR = 9;
const DAY_END_HOUR = 22;
const ROW_HEIGHT = 60; // pixels per hour
const UNASSIGNED_KEY = "__unassigned__";
const ALL_BRANCHES = "all";

// Color palette for bookings (Fresha-style)
const BOOKING_COLORS = [
  { bg: "#BEE3F8", text: "#2A4365", border: "#63B3ED" }, // blue
  { bg: "#FED7D7", text: "#742A2A", border: "#FC8181" }, // rose
  { bg: "#C6F6D5", text: "#22543D", border: "#68D391" }, // green
  { bg: "#FEEBC8", text: "#744210", border: "#F6AD55" }, // amber
  { bg: "#E9D8FD", text: "#44337A", border: "#B794F4" }, // purple
  { bg: "#FBB6CE", text: "#702459", border: "#F687B3" }, // pink
  { bg: "#B2F5EA", text: "#234E52", border: "#4FD1C5" }, // teal
];

function pickColor(key, status, invoiced) {
  const effective = invoiced && status !== "cancelled" ? "completed" : status;
  if (effective === "cancelled") return { bg: "#FEE2E2", text: "#991B1B", border: "#F87171", strike: true };
  if (effective === "completed") return { bg: "#DCFCE7", text: "#14532D", border: "#4ADE80" };
  if (!key) return BOOKING_COLORS[0];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) % BOOKING_COLORS.length;
  return BOOKING_COLORS[h];
}

const parseTimeToMins = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(":").map((x) => parseInt(x) || 0);
  return h * 60 + m;
};

const formatTime = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

// Wave A bugfix — return today as YYYY-MM-DD in the *operator's* local
// timezone (a salon in Doha calling toISOString() at 02:00 local would
// otherwise see "yesterday").
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Returns true if a user is eligible to work in the given branch.
// branch_ids empty/missing = user works in all branches.
const userInBranch = (u, branchId) => {
  if (!branchId || branchId === ALL_BRANCHES) return true;
  const ids = u.branch_ids;
  if (!ids || ids.length === 0) return true;
  return ids.includes(branchId);
};

const estimateBookingDuration = (lineItems, services, packages) => {
  let total = 0;
  lineItems.forEach((it) => {
    const qty = it.qty || 1;
    if (it.kind === "service") {
      const svc = services.find((s) => s.service_id === it.ref_id);
      total += (svc?.duration_min || 30) * qty;
    } else if (it.kind === "package") {
      const pkg = packages.find((p) => p.package_id === it.ref_id);
      if ((it.selected_services || []).length > 0) {
        it.selected_services.forEach((sel) => {
          const svc = services.find((s) => s.service_id === sel.service_id);
          total += (svc?.duration_min || 30) * qty;
        });
      } else {
        total += (pkg?.duration_min || 60) * qty;
      }
    } else {
      total += 30 * qty;
    }
  });
  return Math.max(30, total || 30);
};

const normalizeBookingItemsFromRecord = (b, services, packages) => {
  const fallbackStaff = b.employee_user_id || "";
  if (Array.isArray(b.items) && b.items.length > 0) {
    return b.items.map((i) => ({
      kind: i.kind,
      ref_id: i.ref_id,
      name_en: i.name_en || "",
      name_ar: i.name_ar || "",
      price: Number(i.price || 0),
      qty: i.qty || 1,
      employee_user_id: i.employee_user_id || (i.kind === "service" ? fallbackStaff : ""),
      selected_services: i.selected_services || [],
    }));
  }
  const legacy = [];
  if (b.service_id) {
    const svc = services.find((s) => s.service_id === b.service_id);
    legacy.push({
      kind: "service",
      ref_id: b.service_id,
      name_en: svc?.name_en || b.item_name_en || "",
      name_ar: svc?.name_ar || b.item_name_ar || "",
      price: Number(svc?.price || b.price || 0),
      qty: 1,
      employee_user_id: fallbackStaff,
    });
  } else if (b.package_id) {
    const pkg = packages.find((p) => p.package_id === b.package_id);
    legacy.push({
      kind: "package",
      ref_id: b.package_id,
      name_en: pkg?.name_en || b.item_name_en || "",
      name_ar: pkg?.name_ar || b.item_name_ar || "",
      price: Number(pkg?.price || b.price || 0),
      qty: 1,
      employee_user_id: "",
    });
  } else if (b.item_name_en || b.item_name_ar) {
    legacy.push({
      kind: "service",
      ref_id: b.service_id || "",
      name_en: b.item_name_en || "",
      name_ar: b.item_name_ar || "",
      price: Number(b.price || 0),
      qty: 1,
      employee_user_id: fallbackStaff,
    });
  }
  return legacy;
};

const mergeInvoicePackageStatuses = (items, invoiceItems) => {
  if (!invoiceItems?.length) return items;
  const invPkgLines = invoiceItems.filter((ii) => ii.kind === "package");
  let pkgIdx = 0;
  return items.map((it) => {
    if (it.kind !== "package") return it;
    const invLine = invPkgLines[pkgIdx];
    pkgIdx += 1;
    if (!invLine?.selected_services?.length) return it;
    const invSels = invLine.selected_services;
    const bkSels = it.selected_services || [];
    const merged = invSels.map((invSel, i) => {
      const bkSel = bkSels[i] || {};
      return {
        ...bkSel,
        ...invSel,
        service_id: invSel.service_id,
        status: invSel.status || bkSel.status || "redeemed",
        scheduled_date: invSel.scheduled_date || bkSel.scheduled_date,
        employee_user_id: invSel.employee_user_id || bkSel.employee_user_id,
      };
    });
    return { ...it, selected_services: merged };
  });
};

const bookingItemsToApiPayload = (list) => list.map((i) => ({
  kind: i.kind,
  ref_id: i.ref_id,
  qty: i.qty,
  ...(i.employee_user_id ? { employee_user_id: i.employee_user_id } : {}),
  ...(i.selected_services?.length ? {
    selected_services: i.selected_services.map((s) => ({
      service_id: s.service_id,
      name: s.name || s.name_en || s.name_ar,
      name_en: s.name_en,
      name_ar: s.name_ar,
      ...(s.employee_user_id ? { employee_user_id: s.employee_user_id } : {}),
    })),
  } : {}),
}));

function BookingItemsEditor({
  items,
  services,
  packages,
  staffList,
  disabled,
  lang,
  t,
  onAddService,
  onAddPackage,
  onEditPackage,
  onSwapPending,
  invoicedReadOnly = false,
  onQtyChange,
  onRemove,
  onStaffChange,
}) {
  const [serviceQuery, setServiceQuery] = useState("");
  const [packageQuery, setPackageQuery] = useState("");
  const [serviceDropOpen, setServiceDropOpen] = useState(false);
  const [packageDropOpen, setPackageDropOpen] = useState(false);
  const serviceBoxRef = useRef(null);
  const packageBoxRef = useRef(null);

  const filteredServices = useMemo(
    () => filterServices(services, serviceQuery, 50),
    [services, serviceQuery],
  );
  const filteredPackages = useMemo(
    () => filterPackages(packages, packageQuery, 50),
    [packages, packageQuery],
  );

  useEffect(() => {
    if (disabled) return undefined;
    const onDoc = (e) => {
      if (serviceBoxRef.current && !serviceBoxRef.current.contains(e.target)) setServiceDropOpen(false);
      if (packageBoxRef.current && !packageBoxRef.current.contains(e.target)) setPackageDropOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [disabled]);

  const pickService = (sid) => {
    onAddService(sid);
    setServiceQuery("");
    setServiceDropOpen(false);
  };

  const pickPackage = (pid) => {
    onAddPackage(pid);
    setPackageQuery("");
    setPackageDropOpen(false);
  };

  const itemsTotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  return (
    <div className="space-y-3">
      {!disabled && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div ref={serviceBoxRef}>
            <label className="text-xs text-[var(--text-muted)]">{t("booking_add_service")}</label>
            <div className="relative mt-1">
              <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-[var(--text-muted)] pointer-events-none" />
              <input
                type="text"
                className="input-dark ps-9 pe-8 text-sm"
                placeholder={t("booking_search_service_placeholder")}
                value={serviceQuery}
                onChange={(e) => {
                  setServiceQuery(e.target.value);
                  setServiceDropOpen(true);
                }}
                onFocus={() => setServiceDropOpen(true)}
                data-testid="booking-add-service-search"
              />
              {serviceQuery && (
                <button
                  type="button"
                  onClick={() => { setServiceQuery(""); setServiceDropOpen(false); }}
                  className="absolute top-1/2 -translate-y-1/2 end-2 p-1 text-[var(--text-muted)] hover:text-rose-500"
                >
                  <X size={14} />
                </button>
              )}
              {serviceDropOpen && (
                <div className="absolute z-30 mt-1 left-0 right-0 max-h-52 overflow-auto rounded-md border border-[var(--border)] bg-[var(--surface)] shadow-xl" data-testid="booking-service-search-dropdown">
                  {filteredServices.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-[var(--text-muted)]">{t("no_data")}</div>
                  ) : filteredServices.map((s) => (
                    <button
                      key={s.service_id}
                      type="button"
                      onClick={() => pickService(s.service_id)}
                      className="w-full text-start px-3 py-2 text-sm hover:bg-[var(--surface-2)] flex items-center justify-between gap-2 border-b border-[var(--border-subtle)] last:border-0"
                      data-testid={`booking-service-option-${s.service_id}`}
                    >
                      <span className="truncate">{lang === "ar" ? s.name_ar : s.name_en}</span>
                      <span className="text-[10px] text-[var(--text-muted)] shrink-0">
                        {s.sku ? `${s.sku} · ` : ""}{s.price}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div ref={packageBoxRef}>
            <label className="text-xs text-[var(--text-muted)]">{t("booking_add_package")}</label>
            <div className="relative mt-1">
              <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-[var(--text-muted)] pointer-events-none" />
              <input
                type="text"
                className="input-dark ps-9 pe-8 text-sm"
                placeholder={t("booking_search_package_placeholder")}
                value={packageQuery}
                onChange={(e) => {
                  setPackageQuery(e.target.value);
                  setPackageDropOpen(true);
                }}
                onFocus={() => setPackageDropOpen(true)}
                data-testid="booking-add-package-search"
              />
              {packageQuery && (
                <button
                  type="button"
                  onClick={() => { setPackageQuery(""); setPackageDropOpen(false); }}
                  className="absolute top-1/2 -translate-y-1/2 end-2 p-1 text-[var(--text-muted)] hover:text-rose-500"
                >
                  <X size={14} />
                </button>
              )}
              {packageDropOpen && (
                <div className="absolute z-30 mt-1 left-0 right-0 max-h-52 overflow-auto rounded-md border border-[var(--border)] bg-[var(--surface)] shadow-xl" data-testid="booking-package-search-dropdown">
                  {filteredPackages.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-[var(--text-muted)]">{t("no_data")}</div>
                  ) : filteredPackages.map((p) => (
                    <button
                      key={p.package_id}
                      type="button"
                      onClick={() => pickPackage(p.package_id)}
                      className="w-full text-start px-3 py-2 text-sm hover:bg-[var(--surface-2)] flex items-center justify-between gap-2 border-b border-[var(--border-subtle)] last:border-0"
                      data-testid={`booking-package-option-${p.package_id}`}
                    >
                      <span className="truncate">
                        {lang === "ar" ? p.name_ar : p.name_en}
                        {p.package_type === "pick_n" && (
                          <span className="text-[var(--gold)]"> · {lang === "ar" ? `اختر ${p.pick_count}` : `Pick ${p.pick_count}`}</span>
                        )}
                      </span>
                      <span className="text-[10px] text-[var(--text-muted)] shrink-0">{p.price}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="bg-[var(--bg)] rounded-md p-3 space-y-2">
        <div className="text-xs text-[var(--text-muted)] font-semibold">{lang === "ar" ? "العناصر" : "Items"} ({items.length})</div>
        {items.length === 0 && <div className="text-center text-[var(--text-muted)] text-sm py-3">{lang === "ar" ? "لم تتم إضافة أي عنصر" : "No items added"}</div>}
        {items.map((it, idx) => {
          const pkgMeta = it.kind === "package" ? packages.find((p) => p.package_id === it.ref_id) : null;
          const isPickN = pkgMeta?.package_type === "pick_n" || (it.selected_services || []).length > 0;
          const hasPending = (it.selected_services || []).some((s) => s.status === "pending");
          return (
          <div key={`${it.kind}:${it.ref_id}:${idx}`} className="flex flex-wrap items-center gap-2 bg-[var(--surface)] p-2 rounded-md border border-[var(--border)]">
            <span className="text-xs px-2 py-0.5 rounded bg-[var(--gold)]/10 text-[var(--gold)]">{it.kind === "service" ? (lang === "ar" ? "خدمة" : "Svc") : (lang === "ar" ? "باقة" : "Pkg")}</span>
            <div className="flex-1 min-w-[120px]">
              <div className="text-sm truncate">{lang === "ar" ? it.name_ar : it.name_en}</div>
              {(it.selected_services || []).map((sel) => (
                <div key={sel.service_id} className="text-[10px] text-[var(--text-muted)] truncate ps-1 border-s border-[var(--gold)]/30 flex items-center gap-1 flex-wrap">
                  <span>• {selectedServiceLabel(sel, lang)}</span>
                  {sel.status === "pending" && (
                    <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">{lang === "ar" ? "معلق" : "Pending"}</span>
                  )}
                  {sel.status && sel.status !== "pending" && (
                    <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800">{lang === "ar" ? "منجزة" : "Done"}</span>
                  )}
                </div>
              ))}
            </div>
            {it.kind === "service" && (
              <select
                className="input-dark text-xs min-w-[120px] max-w-[160px]"
                value={it.employee_user_id || ""}
                onChange={(e) => onStaffChange(idx, e.target.value)}
                disabled={disabled}
                data-testid={`booking-line-staff-${idx}`}
              >
                <option value="">{t("booking_line_staff_unassigned")}</option>
                {staffList.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.username}</option>)}
              </select>
            )}
            {isPickN && !disabled && onEditPackage && (
              <button
                type="button"
                onClick={() => onEditPackage(idx)}
                className="text-[10px] px-2 py-1 rounded border border-[var(--gold)]/40 text-[var(--gold)]"
                data-testid={`booking-edit-package-${idx}`}
              >
                {lang === "ar" ? "تغيير الخدمات" : "Change services"}
              </button>
            )}
            {invoicedReadOnly && isPickN && hasPending && onSwapPending && (
              <button
                type="button"
                onClick={() => onSwapPending(idx)}
                className="text-[10px] px-2 py-1 rounded border border-amber-400/60 text-amber-800 bg-amber-50"
                data-testid={`booking-swap-pending-${idx}`}
              >
                {lang === "ar" ? "استبدال المعلقة" : "Swap pending"}
              </button>
            )}
            {!disabled && (
              <>
                <div className="flex items-center gap-1 bg-[var(--bg)] rounded">
                  <button type="button" onClick={() => onQtyChange(idx, -1)} className="p-1"><Minus size={12} /></button>
                  <span className="px-2 text-sm">{it.qty}</span>
                  <button type="button" onClick={() => onQtyChange(idx, 1)} className="p-1"><Plus size={12} /></button>
                </div>
                <button type="button" onClick={() => onRemove(idx)} className="p-1 text-rose-500"><X size={14} /></button>
              </>
            )}
            {disabled && it.qty > 1 && <span className="text-xs text-[var(--text-muted)]">×{it.qty}</span>}
            <span className="text-sm gold-text font-semibold w-16 text-end ms-auto">{(it.price * it.qty).toFixed(0)}</span>
          </div>
        );})}
        {items.length > 0 && (
          <div className="flex justify-between border-t border-[var(--border)] pt-2 mt-2 font-semibold">
            <span>{t("total")}</span>
            <span className="gold-text">{itemsTotal.toFixed(2)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

const rotaWarningText = (check, t, lang) => {
  if (!check || check.on_duty !== false || !check.warning) return null;
  if (check.warning === "not_scheduled") return t("rota_warning_not_scheduled");
  if (check.warning === "outside_shift") {
    const shift = check.shift;
    const shiftLabel = shift?.start && shift?.end
      ? `${shift.start}–${shift.end}`
      : "";
    return shiftLabel
      ? `${t("rota_warning_outside_shift")} (${shiftLabel})`
      : t("rota_warning_outside_shift");
  }
  return null;
};

export default function Bookings() {
  const { t, lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const { branches, activeBranchId } = useBranch();
  const { user, isSalonAdmin } = useAuth();
  const ownerBoard = canViewBookingsOwnerBoard(user?.role);
  const [bookings, setBookings] = useState([]);
  const [overdueBookings, setOverdueBookings] = useState([]);
  const [overdueExpanded, setOverdueExpanded] = useState(true);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem("bookings_owner_view") || "calendar";
    } catch {
      return "calendar";
    }
  }); // calendar | list — owner only
  const [deskSearch, setDeskSearch] = useState("");
  const [deskSearchResults, setDeskSearchResults] = useState([]);
  const [deskSearchLoading, setDeskSearchLoading] = useState(false);
  const [deskSheetBooking, setDeskSheetBooking] = useState(null);
  const [laterTodayExpanded, setLaterTodayExpanded] = useState(false);
  const [selectedDate, setSelectedDate] = useState(todayISO());
  // Page-level branch filter. Defaults to the global active branch. Admins
  // may also choose "all" to view bookings across every branch.
  const [pageBranch, setPageBranch] = useState(activeBranchId || "");

  // Keep page branch in sync with the global active branch on first load
  useEffect(() => {
    if (!pageBranch && activeBranchId) setPageBranch(activeBranchId);
  }, [activeBranchId, pageBranch]);

  // Operational roles: single branch only (no all-branches desk view)
  useEffect(() => {
    if (ownerBoard) return;
    if (activeBranchId && pageBranch !== activeBranchId) {
      setPageBranch(activeBranchId);
    }
  }, [ownerBoard, activeBranchId, pageBranch]);

  useEffect(() => {
    if (ownerBoard) {
      try {
        localStorage.setItem("bookings_owner_view", view);
      } catch { /* ignore */ }
    }
  }, [view, ownerBoard]);

  // New booking modal
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", customer_name: "", customer_phone: "", date: "", time_slot: "", notes: "", employee_user_id: "", branch_id: "" });
  const [items, setItems] = useState([]);

  // Inline "create new customer" panel inside the booking modal
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", phone: "", email: "", language_pref: "ar", notes: "" });
  const [savingCust, setSavingCust] = useState(false);
  const [custQuery, setCustQuery] = useState("");
  const [custDropOpen, setCustDropOpen] = useState(false);
  const custBoxRef = useRef(null);

  // Edit booking modal
  const [editBooking, setEditBooking] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editItems, setEditItems] = useState([]);
  const [editModalStaff, setEditModalStaff] = useState([]);
  const [rotaCheck, setRotaCheck] = useState(null);
  const [editRotaCheck, setEditRotaCheck] = useState(null);
  const [pickPackageModal, setPickPackageModal] = useState(null);

  const branchLabel = useCallback((b) => (lang === "ar" ? (b.name_ar || b.name_en) : (b.name_en || b.name_ar)) || b.branch_id, [lang]);

  const customerNameById = useMemo(() => {
    const m = {};
    customers.forEach((c) => { m[c.customer_id] = c.name; });
    return m;
  }, [customers]);

  const filteredCustomers = useMemo(
    () => filterCustomers(customers, custQuery, 50),
    [customers, custQuery],
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (custBoxRef.current && !custBoxRef.current.contains(e.target)) setCustDropOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const displayCustomerName = useCallback((b) => {
    if (!b) return "";
    return (b.customer_name || "").trim() || customerNameById[b.customer_id] || "";
  }, [customerNameById]);

  const bookingSessionState = useCallback((b) => deriveBookingSessionState(b, bookingTodayISO()), []);
  const liveSessionMinutes = useCallback((b) => deriveLiveSessionMinutes(b), []);

  const load = useCallback(async () => {
    const branchParam = pageBranch === ALL_BRANCHES ? "all" : pageBranch;
    const bookingsUrl = branchParam
      ? `/bookings?date=${selectedDate}&branch=${encodeURIComponent(branchParam)}`
      : `/bookings?date=${selectedDate}`;
    const overdueUrl = branchParam
      ? `/bookings?overdue=true&branch=${encodeURIComponent(branchParam)}`
      : `/bookings?overdue=true`;
    // Users for calendar columns: filtered by page branch so cashiers in
    // other branches don't pollute the agenda. For "all branches" admin
    // view we fetch every staff member.
    const usersUrl = pageBranch && pageBranch !== ALL_BRANCHES
      ? `/staff-assignable?branch_id=${encodeURIComponent(pageBranch)}`
      : "/staff-assignable";
    const safe = (p) => p.catch(() => ({ data: [] }));
    const [b, od, s, p, c, u] = await Promise.all([
      api.get(bookingsUrl),
      safe(api.get(overdueUrl)),
      safe(api.get("/services")),
      safe(api.get("/packages")),
      safe(api.get("/customers")),
      safe(api.get(usersUrl)),
    ]);
    setBookings(b.data);
    setOverdueBookings(od.data || []);
    setServices(s.data); setPackages(p.data); setCustomers(c.data); setUsers(u.data);
  }, [selectedDate, pageBranch]);
  useEffect(() => { load(); }, [load]);

  // Desk search — all dates (server-side), debounced
  useEffect(() => {
    if (ownerBoard) return undefined;
    const q = deskSearch.trim();
    if (!q) {
      setDeskSearchResults([]);
      setDeskSearchLoading(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setDeskSearchLoading(true);
      try {
        const branchParam = pageBranch === ALL_BRANCHES ? "all" : pageBranch;
        const params = { search: q };
        if (branchParam) params.branch = branchParam;
        const r = await api.get("/bookings", { params });
        if (!cancelled) setDeskSearchResults(Array.isArray(r.data) ? r.data : []);
      } catch {
        if (!cancelled) setDeskSearchResults([]);
      } finally {
        if (!cancelled) setDeskSearchLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [deskSearch, pageBranch, ownerBoard]);

  // Staff columns = users with role staff/manager (not viewer/admin)
  const staffList = useMemo(() => users.filter((u) => ["staff", "manager"].includes(u.role)), [users]);

  // Staff filtered by the *modal's* selected branch (for the booking form's
  // employee dropdown). When the form's branch changes, this list shrinks.
  // Note: when in "all-branches" page view we still need to resolve users
  // for the modal's chosen branch — fetch separately when it differs.
  const [modalStaff, setModalStaff] = useState([]);
  useEffect(() => {
    if (!open) return;
    if (!form.branch_id || form.branch_id === ALL_BRANCHES) {
      setModalStaff(staffList);
      return;
    }
    // If the page branch already equals the modal branch, reuse the
    // already-loaded users list (no extra request).
    if (form.branch_id === pageBranch) {
      setModalStaff(staffList.filter((u) => userInBranch(u, form.branch_id)));
      return;
    }
    let cancelled = false;
    api.get(`/staff-assignable?branch_id=${encodeURIComponent(form.branch_id)}`)
      .then((r) => { if (!cancelled) setModalStaff((r.data || []).filter((u) => ["staff", "manager"].includes(u.role))); })
      .catch(() => { if (!cancelled) setModalStaff([]); });
    return () => { cancelled = true; };
  }, [open, form.branch_id, pageBranch, staffList]);

  useEffect(() => {
    if (!editBooking) return;
    const branchId = editBooking.branch_id;
    if (!branchId || branchId === ALL_BRANCHES) {
      setEditModalStaff(staffList);
      return;
    }
    if (branchId === pageBranch) {
      setEditModalStaff(staffList.filter((u) => userInBranch(u, branchId)));
      return;
    }
    let cancelled = false;
    api.get(`/staff-assignable?branch_id=${encodeURIComponent(branchId)}`)
      .then((r) => { if (!cancelled) setEditModalStaff((r.data || []).filter((u) => ["staff", "manager"].includes(u.role))); })
      .catch(() => { if (!cancelled) setEditModalStaff([]); });
    return () => { cancelled = true; };
  }, [editBooking, pageBranch, staffList]);

  // When the modal's branch changes, drop the previously-selected employee
  // if they're not eligible for the new branch (recommended UX choice c).
  useEffect(() => {
    if (!form.employee_user_id) return;
    if (!form.branch_id) return;
    const stillValid = modalStaff.some((u) => u.user_id === form.employee_user_id);
    if (!stillValid) {
      setForm((f) => ({ ...f, employee_user_id: "" }));
    }
  }, [modalStaff, form.branch_id]); // eslint-disable-line

  const shiftDate = (days) => {
    // Wave A bugfix — timezone-safe day shift.
    const [y, m, d] = selectedDate.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + days);
    const next = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
    setSelectedDate(next);
  };

  // ===== New Booking logic =====
  const [slotCap, setSlotCap] = useState([]);
  useEffect(() => {
    if (!form.date) return;
    api.get(`/public/slots?date=${form.date}`).then((r) => setSlotCap(r.data.slots || [])).catch(() => {});
  }, [form.date]);

  const addServiceToList = (setList, sid, defaultStaff, catalog) => {
    const svc = catalog.find((s) => s.service_id === sid);
    if (!svc) return;
    setList((prev) => {
      const ex = prev.find((i) => i.kind === "service" && i.ref_id === sid && (i.employee_user_id || "") === (defaultStaff || ""));
      if (ex) return prev.map((i) => (i === ex ? { ...i, qty: i.qty + 1 } : i));
      return [...prev, {
        kind: "service",
        ref_id: sid,
        name_en: svc.name_en,
        name_ar: svc.name_ar,
        price: svc.price,
        qty: 1,
        employee_user_id: defaultStaff || "",
      }];
    });
  };

  const addPackageToList = (setList, pid, catalog) => {
    const pkg = catalog.find((p) => p.package_id === pid);
    if (!pkg) return;
    setList((prev) => {
      const ex = prev.find((i) => i.kind === "package" && i.ref_id === pid && !(i.selected_services || []).length);
      if (ex) return prev.map((i) => (i === ex ? { ...i, qty: i.qty + 1 } : i));
      return [...prev, {
        kind: "package",
        ref_id: pid,
        name_en: pkg.name_en,
        name_ar: pkg.name_ar,
        price: pkg.price,
        qty: 1,
        employee_user_id: "",
      }];
    });
  };

  const openPickPackageModal = (pkg, staffForModal, initialSelections, onConfirm, extra = {}) => {
    setPickPackageModal({
      pkg,
      staff: staffForModal,
      initialSelections: initialSelections || [],
      confirmLabel: extra.confirmLabel || (lang === "ar"
        ? (initialSelections?.length ? "حفظ الاختيار" : "إضافة للحجز")
        : (initialSelections?.length ? "Save selection" : "Add to booking")),
      pendingSwapMode: !!extra.pendingSwapMode,
      onConfirm,
    });
  };

  const requestPendingSwap = (idx) => {
    const it = editItems[idx];
    if (!it || it.kind !== "package" || !editBooking) return;
    const pkg = packages.find((p) => p.package_id === it.ref_id);
    if (!pkg || pkg.package_type !== "pick_n") return;
    const hasPending = (it.selected_services || []).some((s) => s.status === "pending");
    if (!hasPending) {
      toast.error(lang === "ar" ? "لا توجد خدمات معلقة للاستبدال" : "No pending services to swap");
      return;
    }
    const staffForModal = editModalStaff.length
      ? editModalStaff
      : staffList.filter((u) => userInBranch(u, editBooking.branch_id));
    openPickPackageModal(
      pkg,
      staffForModal,
      it.selected_services || [],
      async (selections) => {
        try {
          const r = await api.patch(`/bookings/${editBooking.booking_id}/pick-n-pending-services`, {
            item_index: idx,
            selected_services: selections.map((s) => ({
              service_id: s.service_id,
              employee_user_id: s.employee_user_id || null,
              status: s.status || "pending",
              ...(s.scheduled_date ? { scheduled_date: s.scheduled_date } : {}),
            })),
          });
          const updated = r.data?.selected_services || selections;
          setEditItems((prev) => prev.map((row, i) => (
            i === idx ? { ...row, selected_services: updated } : row
          )));
          toast.success(lang === "ar" ? "تم تحديث الخدمات المعلقة" : "Pending services updated");
        } catch (e) {
          toast.error(localizeError(e, t, lang));
        }
      },
      {
        pendingSwapMode: true,
        confirmLabel: lang === "ar" ? "حفظ التغييرات" : "Save changes",
      },
    );
  };

  const requestPackageAdd = (setList, staffForModal) => (pid) => {
    const pkg = packages.find((p) => p.package_id === pid);
    if (!pkg) return;
    if (pkg.package_type === "pick_n") {
      openPickPackageModal(pkg, staffForModal, [], (selections) => {
        setList((prev) => [...prev, {
          kind: "package",
          ref_id: pid,
          name_en: pkg.name_en,
          name_ar: pkg.name_ar,
          price: pkg.price,
          qty: 1,
          employee_user_id: "",
          selected_services: selections,
        }]);
      });
      return;
    }
    addPackageToList(setList, pid, packages);
  };

  const requestPackageEdit = (list, setList, staffForModal) => (idx) => {
    const it = list[idx];
    if (!it || it.kind !== "package") return;
    const pkg = packages.find((p) => p.package_id === it.ref_id);
    if (!pkg || pkg.package_type !== "pick_n") return;
    openPickPackageModal(pkg, staffForModal, it.selected_services || [], (selections) => {
      setList((prev) => prev.map((row, i) => (i === idx ? { ...row, selected_services: selections } : row)));
    });
  };

  const updateListItemQty = (setList, idx, d) => setList((p) => p.map((it, i) => (i === idx ? { ...it, qty: Math.max(1, it.qty + d) } : it)));
  const removeListItem = (setList, idx) => setList((p) => p.filter((_, i) => i !== idx));
  const setListItemStaff = (setList, idx, userId) => setList((p) => p.map((it, i) => (i === idx ? { ...it, employee_user_id: userId } : it)));

  const addServiceItem = (sid) => addServiceToList(setItems, sid, form.employee_user_id, services);
  const addPackageItem = requestPackageAdd(setItems, modalStaff);
  const editPackageItem = requestPackageEdit(items, setItems, modalStaff);
  const updateItemQty = (idx, d) => updateListItemQty(setItems, idx, d);
  const removeItem = (idx) => removeListItem(setItems, idx);
  const setItemStaff = (idx, userId) => setListItemStaff(setItems, idx, userId);

  const addEditServiceItem = (sid) => addServiceToList(setEditItems, sid, editForm.employee_user_id, services);
  const addEditPackageItem = requestPackageAdd(
    setEditItems,
    editModalStaff.length ? editModalStaff : staffList.filter((u) => userInBranch(u, editBooking?.branch_id)),
  );
  const editEditPackageItem = requestPackageEdit(
    editItems,
    setEditItems,
    editModalStaff.length ? editModalStaff : staffList.filter((u) => userInBranch(u, editBooking?.branch_id)),
  );

  const newBookingDuration = useMemo(
    () => estimateBookingDuration(items, services, packages),
    [items, services, packages],
  );

  useEffect(() => {
    if (!open || !form.employee_user_id || !form.date || !form.time_slot) {
      setRotaCheck(null);
      return;
    }
    let cancelled = false;
    const q = new URLSearchParams({
      employee_user_id: form.employee_user_id,
      date_str: form.date,
      time_slot: form.time_slot,
      duration_min: String(newBookingDuration),
    });
    api.get(`/staff-rota/check?${q}`)
      .then((r) => { if (!cancelled) setRotaCheck(r.data); })
      .catch(() => { if (!cancelled) setRotaCheck(null); });
    return () => { cancelled = true; };
  }, [open, form.employee_user_id, form.date, form.time_slot, newBookingDuration]);

  const editBookingDuration = useMemo(
    () => estimateBookingDuration(editItems, services, packages),
    [editItems, services, packages],
  );

  useEffect(() => {
    if (!editBooking || !editForm.employee_user_id || !editForm.date || !editForm.time_slot) {
      setEditRotaCheck(null);
      return;
    }
    let cancelled = false;
    const q = new URLSearchParams({
      employee_user_id: editForm.employee_user_id,
      date_str: editForm.date,
      time_slot: editForm.time_slot,
      duration_min: String(editBookingDuration),
    });
    api.get(`/staff-rota/check?${q}`)
      .then((r) => { if (!cancelled) setEditRotaCheck(r.data); })
      .catch(() => { if (!cancelled) setEditRotaCheck(null); });
    return () => { cancelled = true; };
  }, [editBooking, editForm.employee_user_id, editForm.date, editForm.time_slot, editBookingDuration]);

  const openNewBooking = () => {
    // Default the new booking to the page's branch unless we're in "all"
    // mode — then default to the global active branch so the cashier still
    // has a sensible starting point.
    const defaultBranch = (pageBranch && pageBranch !== ALL_BRANCHES) ? pageBranch : activeBranchId || "";
    setForm({ customer_id: "", customer_name: "", customer_phone: "", date: selectedDate, time_slot: "", notes: "", employee_user_id: "", branch_id: defaultBranch });
    setItems([]);
    setRotaCheck(null);
    setNewCustOpen(false);
    setNewCust({ name: "", phone: "", email: "", language_pref: "ar", notes: "" });
    setCustQuery("");
    setCustDropOpen(false);
    setOpen(true);
  };

  const selectBookingCustomer = (c) => {
    if (!c) {
      setForm((f) => ({ ...f, customer_id: "", customer_name: "", customer_phone: "" }));
      setCustQuery("");
      setCustDropOpen(false);
      return;
    }
    setForm((f) => ({
      ...f,
      customer_id: c.customer_id,
      customer_name: c.name || "",
      customer_phone: c.phone || "",
    }));
    setCustQuery(c.name + (c.phone ? ` · ${c.phone}` : ""));
    setCustDropOpen(false);
  };

  const clearBookingCustomer = () => selectBookingCustomer(null);

  // Inline customer creation — POST to /customers, then auto-select.
  const saveNewCustomer = async () => {
    if (!newCust.name.trim() || !newCust.phone.trim()) {
      return toast.error(lang === "ar" ? "الاسم والهاتف مطلوبان" : "Name and phone are required");
    }
    setSavingCust(true);
    try {
      let allowDuplicate = false;
      const dup = await resolveNewCustomerPhoneDuplicate({
        api,
        phone: newCust.phone,
        lang,
        confirm,
      });
      if (!dup.proceed) return;
      allowDuplicate = dup.allowDuplicate;
      const r = await api.post("/customers", newCust, {
        params: allowDuplicate ? { allow_duplicate: true } : {},
      });
      const c = r.data;
      setCustomers((prev) => [c, ...prev]);
      setForm((f) => ({ ...f, customer_id: c.customer_id, customer_name: c.name, customer_phone: c.phone }));
      setCustQuery(c.name + (c.phone ? ` · ${c.phone}` : ""));
      setCustDropOpen(false);
      setNewCustOpen(false);
      setNewCust({ name: "", phone: "", email: "", language_pref: "ar", notes: "" });
      toast.success(lang === "ar" ? "تم إنشاء العميل" : "Customer created");
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "ar" ? "تعذر الإنشاء" : "Failed"));
    } finally {
      setSavingCust(false);
    }
  };

  const submit = async () => {
    if (items.length === 0) return toast.error(lang === "ar" ? "أضف خدمة أو باقة" : "Add item");
    if (!form.customer_name || !form.customer_phone || !form.date || !form.time_slot) return toast.error(t("required_field"));
    if (branches.length > 1 && !form.branch_id) return toast.error(lang === "ar" ? "اختر الفرع" : "Select a branch");
    const missingStaff = items.some((i) => i.kind === "service" && !i.employee_user_id);
    if (missingStaff) return toast.error(t("booking_staff_required"));
    try {
      await api.post("/bookings", {
        ...form,
        items: bookingItemsToApiPayload(items),
      });
      toast.success(t("book_success"));
      setOpen(false); setItems([]);
      setForm({ customer_id: "", customer_name: "", customer_phone: "", date: "", time_slot: "", notes: "", employee_user_id: "", branch_id: "" });
      setCustQuery("");
      setCustDropOpen(false);
      load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  // ===== Edit booking =====
  const isBookingInvoiced = (b) => !!(b?.invoiced || b?.invoice_id);

  const effectiveBookingStatus = (b) => {
    if (!b) return "confirmed";
    if (b.status === "cancelled") return "cancelled";
    if (b.status === "no_show") return "no_show";
    if (isBookingInvoiced(b)) return "completed";
    if (isBookingStale(b, todayISO())) return "stale";
    return b.status || "confirmed";
  };

  const openEdit = async (b) => {
    setEditBooking(b);
    setEditForm({
      customer_name: b.customer_name || "",
      customer_phone: b.customer_phone || "",
      date: b.date,
      time_slot: b.time_slot,
      employee_user_id: b.employee_user_id || "",
      notes: b.notes || "",
      status: b.status,
    });
    let items = normalizeBookingItemsFromRecord(b, services, packages);
    if ((b.invoiced || b.invoice_id) && b.invoice_id) {
      try {
        const r = await api.get(`/invoices/${b.invoice_id}`);
        items = mergeInvoicePackageStatuses(items, r.data?.items);
      } catch {
        // Fall back to booking items if invoice cannot be loaded.
      }
    }
    setEditItems(items);
  };
  const saveEdit = async () => {
    if (editItems.length === 0) return toast.error(lang === "ar" ? "أضف خدمة أو باقة" : "Add item");
    const missingStaff = editItems.some((i) => i.kind === "service" && !i.employee_user_id);
    if (missingStaff) return toast.error(t("booking_staff_required"));
    try {
      await api.put(`/bookings/${editBooking.booking_id}`, {
        ...editForm,
        items: bookingItemsToApiPayload(editItems),
      });
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      setEditBooking(null);
      setEditItems([]);
      load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };
  const changeStatus = async (status, bookingId) => {
    const id = bookingId || editBooking?.booking_id;
    if (!id) return;
    try {
      await api.put(`/bookings/${id}/status?status=${status}`);
      toast.success(lang === "ar" ? "تم التحديث" : "Updated");
      if (!bookingId) setEditBooking(null);
      load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  const dismissOverdueBooking = async (b, status) => {
    const isNoShow = status === "no_show";
    const ok = await confirm({
      title: isNoShow ? t("booking_no_show_confirm_title") : t("booking_cancel_confirm_title"),
      message: isNoShow ? t("booking_no_show_confirm_message") : t("booking_cancel_confirm_message"),
      confirmLabel: isNoShow ? t("booking_no_show") : t("cancel_booking"),
      variant: "danger",
    });
    if (!ok) return;
    await changeStatus(status, b.booking_id);
  };

  const checkInCustomer = async (bookingId) => {
    const id = bookingId || editBooking?.booking_id;
    if (!id) return;
    try {
      const r = await api.post(`/bookings/${id}/check-in`);
      toast.success(t("customer_arrived_success"));
      if (editBooking?.booking_id === id) setEditBooking(r.data);
      setActionsFor(null);
      setDeskSheetBooking(null);
      load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  const startService = async (bookingId) => {
    const id = bookingId || editBooking?.booking_id;
    if (!id) return;
    try {
      const r = await api.post(`/bookings/${id}/start-service`);
      toast.success(t("service_started_success"));
      if (editBooking?.booking_id === id) setEditBooking(r.data);
      setActionsFor(null);
      setDeskSheetBooking(null);
      load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  const formatTs = (ts) => {
    if (!ts) return "";
    try {
      return new Date(ts).toLocaleString(lang === "ar" ? "ar-EG" : "en-US", {
        dateStyle: "short", timeStyle: "short",
      });
    } catch { return ts; }
  };

  const [visitTick, setVisitTick] = useState(0);
  const hasLiveSessions = useMemo(() => (
    bookings.some((b) => {
      const st = bookingSessionState(b);
      return st === "waiting" || st === "in_service" || st === "in_progress";
    })
  ), [bookings, bookingSessionState]);

  useEffect(() => {
    const editLive = editBooking?.arrived_at && !editBooking?.paid_at && !editBooking?.invoiced;
    if (!hasLiveSessions && !editLive) return undefined;
    const tmr = setInterval(() => setVisitTick((x) => x + 1), 60000);
    return () => clearInterval(tmr);
  }, [hasLiveSessions, editBooking?.arrived_at, editBooking?.paid_at, editBooking?.invoiced, editBooking?.service_started_at]);
  const quickDelete = async (bookingId) => {
    const ok = await confirm({
      title: lang === "ar" ? "حذف الحجز؟" : "Delete booking?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/bookings/${bookingId}`);
      toast.success(lang === "ar" ? "تم الحذف" : "Deleted");
      load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };
  const deleteBooking = async () => {
    const ok = await confirm({
      title: lang === "ar" ? "حذف الحجز؟" : "Delete booking?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/bookings/${editBooking.booking_id}`);
    setEditBooking(null); load();
  };

  // Smart Actions popover state (booking_id of the open menu)
  const [actionsFor, setActionsFor] = useState(null);
  React.useEffect(() => {
    const onDoc = (e) => {
      if (!e.target.closest?.("[data-smart-actions]")) setActionsFor(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // ===== Calendar rendering =====
  const hours = useMemo(() => {
    const out = [];
    for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) out.push(h);
    return out;
  }, []);

  const gridHeight = (DAY_END_HOUR - DAY_START_HOUR) * ROW_HEIGHT;

  // Group bookings by employee
  const bookingsByEmployee = useMemo(() => {
    const map = {};
    const staffIds = staffList.map((s) => s.user_id);
    staffIds.forEach((sid) => (map[sid] = []));
    map[UNASSIGNED_KEY] = [];
    bookings.forEach((b) => {
      const key = b.employee_user_id && staffIds.includes(b.employee_user_id) ? b.employee_user_id : UNASSIGNED_KEY;
      map[key].push(b);
    });
    return map;
  }, [bookings, staffList]);

  const columns = useMemo(() => {
    const cols = staffList.map((s) => ({ id: s.user_id, name: s.name || s.username, role: s.role }));
    if ((bookingsByEmployee[UNASSIGNED_KEY] || []).length > 0 || cols.length === 0) {
      cols.push({ id: UNASSIGNED_KEY, name: lang === "ar" ? "غير مُسند" : "Unassigned", role: "" });
    }
    return cols;
  }, [staffList, bookingsByEmployee, lang]);

  const renderBookingBlock = (b) => {
    const startMin = parseTimeToMins(b.time_slot) - DAY_START_HOUR * 60;
    const dur = Math.max(30, b.duration_min || 30);
    const top = (startMin / 60) * ROW_HEIGHT;
    const height = (dur / 60) * ROW_HEIGHT - 2;
    const color = pickColor(b.booking_id, b.status, b.invoiced || b.invoice_id);
    const custName = displayCustomerName(b) || (lang === "ar" ? "بدون اسم" : "Walk-in");
    const sess = bookingSessionState(b);
    const live = liveSessionMinutes(b);
    void visitTick;
    const itemsPreview = (b.items && b.items.length > 0
      ? b.items.map((i) => (lang === "ar" ? i.name_ar : i.name_en)).join(" + ")
      : (lang === "ar" ? b.item_name_ar : b.item_name_en) || "");
    return (
      <div
        key={b.booking_id}
        data-testid={`booking-block-${b.booking_id}`}
        className="absolute inset-x-1 rounded-md p-2 text-start overflow-hidden hover:shadow-lg transition cursor-pointer z-10 hover:scale-[1.02] group"
        style={{
          top: `${top}px`,
          height: `${height}px`,
          background: color.bg,
          color: color.text,
          borderInlineStart: `4px solid ${color.border}`,
          textDecoration: color.strike ? "line-through" : "none",
        }}
      >
        <button
          type="button"
          onClick={() => openEdit(b)}
          className="w-full h-full text-start"
          style={{ color: color.text }}
        >
          <div className="text-[10px] opacity-80 font-mono">{b.time_slot} · {b.duration_min || 30}{lang === "ar" ? "د" : "m"}</div>
          <div className="text-sm font-bold truncate leading-tight mt-0.5" title={custName}>{custName}</div>
          <div className="text-[11px] truncate opacity-90 leading-tight mt-0.5">{itemsPreview}</div>
          {(b.invoiced || b.paid_at || bookingSessionState(b) === "completed") && (
            <span className="text-[8px] px-1.5 py-px rounded-full font-bold bg-emerald-700 text-white">
              {lang === "ar" ? "مكتمل" : "Done"}
            </span>
          )}
          {(sess === "waiting" || sess === "in_service") && (
            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
              <span className={`text-[8px] px-1.5 py-px rounded-full font-bold ${sess === "waiting" ? "bg-amber-600 text-white" : "bg-violet-700 text-white"}`}>
                {sess === "waiting" ? t("waiting") : t("in_service")}
              </span>
              {live.total != null && (
                <span className="text-[9px] font-mono opacity-90">{live.total}{lang === "ar" ? "د" : "m"}</span>
              )}
            </div>
          )}
          {b.total_price > 0 && (
            <div className="text-[10px] mt-auto opacity-75 flex items-center gap-1">
              <span>{b.total_price} {lang === "ar" ? "ر.ق" : "QAR"}</span>
              {b.invoiced && (
                <span
                  className="px-1.5 py-px rounded-full bg-emerald-600 text-white font-bold text-[9px]"
                  title={b.invoice_number || ""}
                  data-testid={`booking-invoiced-${b.booking_id}`}
                >
                  {lang === "ar" ? "مفوتر" : "INVOICED"}
                </span>
              )}
            </div>
          )}
        </button>

        {/* Smart actions trigger */}
        <div data-smart-actions className="absolute top-1 end-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition">
          <button
            type="button"
            data-testid={`smart-actions-${b.booking_id}`}
            onClick={(e) => { e.stopPropagation(); setActionsFor(actionsFor === b.booking_id ? null : b.booking_id); }}
            className="p-2 rounded-full bg-white/95 hover:bg-white text-stone-700 shadow min-h-[36px] min-w-[36px] flex items-center justify-center"
            title={lang === "ar" ? "إجراءات سريعة" : "Quick actions"}
          >
            <MoreVertical size={14} />
          </button>
          {actionsFor === b.booking_id && (
            <div
              className="absolute end-0 top-9 z-30 min-w-[200px] rounded-md bg-white shadow-xl border border-stone-200 py-1 text-stone-800"
              data-testid={`smart-actions-menu-${b.booking_id}`}
              onMouseDown={onModalPanelMouseDown}
            >
              <BookingQuickActions
                b={b}
                lang={lang}
                t={t}
                isBookingInvoiced={isBookingInvoiced}
                onClose={() => setActionsFor(null)}
                onOpenEdit={openEdit}
                onChangeStatus={changeStatus}
                onCheckIn={checkInCustomer}
                onStartService={startService}
                onDelete={quickDelete}
                buttonClassName="w-full px-3 py-2 text-start text-xs hover:bg-stone-50 flex items-center gap-2 min-h-[40px]"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const statusBadge = (s) => {
    const map = {
      confirmed: { bg: "bg-blue-100", text: "text-blue-700", label: lang === "ar" ? "مؤكد" : "Confirmed" },
      pending: { bg: "bg-amber-100", text: "text-amber-700", label: lang === "ar" ? "معلق" : "Pending" },
      completed: { bg: "bg-green-100", text: "text-green-700", label: lang === "ar" ? "مكتمل" : "Completed" },
      cancelled: { bg: "bg-rose-100", text: "text-rose-700", label: lang === "ar" ? "ملغي" : "Cancelled" },
      no_show: { bg: "bg-stone-200", text: "text-stone-700", label: lang === "ar" ? "لم يحضر" : "No-show" },
      stale: { bg: "bg-amber-100", text: "text-amber-800", label: lang === "ar" ? "متأخر" : "Overdue" },
    };
    const c = map[s] || map.confirmed;
    return <span className={`text-xs px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}>{c.label}</span>;
  };

  // Resolve a branch name from a branch_id for display in the list / cells.
  const branchNameById = useMemo(() => {
    const m = {};
    branches.forEach((b) => { m[b.branch_id] = branchLabel(b); });
    return m;
  }, [branches, branchLabel]);

  const showBranchColumn = pageBranch === ALL_BRANCHES;

  const staffNameById = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.user_id] = u.name || u.username; });
    return m;
  }, [users]);

  const overdueDaysLabel = useCallback((dateStr) => {
    if (!dateStr) return "";
    const today = new Date(`${todayISO()}T00:00:00`);
    const d = new Date(`${dateStr}T00:00:00`);
    const diff = Math.round((today - d) / 86400000);
    if (diff <= 0) return lang === "ar" ? "اليوم" : "Today";
    if (diff === 1) return lang === "ar" ? "أمس" : "Yesterday";
    return lang === "ar" ? `منذ ${diff} أيام` : `${diff} days ago`;
  }, [lang]);

  const goToBookingDay = (dateStr) => {
    setSelectedDate(dateStr);
    setOverdueExpanded(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openOverdueBooking = (b) => {
    openEdit(b);
    setOverdueExpanded(false);
  };

  const deskIsToday = selectedDate === todayISO();

  const currentTimeMins = useMemo(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  }, [visitTick, selectedDate, deskIsToday]);

  const deskGroups = useMemo(() => {
    const q = deskSearch.trim().toLowerCase();
    const byTime = (a, b) => (a.time_slot || "").localeCompare(b.time_slot || "");
    const byDateTime = (a, b) => (a.date || "").localeCompare(b.date || "") || byTime(a, b);
    const source = q ? deskSearchResults : bookings;
    const active = source.filter((b) => b.status !== "cancelled" && b.status !== "no_show");

    if (q) {
      const sorted = [...active].sort(byDateTime);
      return { inService: [], waiting: [], upNext: [], later: [], all: sorted, searchMode: true };
    }

    const inService = [];
    const waiting = [];
    const upNext = [];
    const later = [];

    if (!deskIsToday) {
      const all = [...active].sort(byTime);
      return { inService, waiting, upNext, later: all, all, searchMode: false };
    }

    active.forEach((b) => {
      if (isBookingInvoiced(b)) return;
      const sess = bookingSessionState(b);
      if (sess === "in_service" || sess === "in_progress") {
        inService.push(b);
        return;
      }
      if (sess === "waiting") {
        waiting.push(b);
        return;
      }
      if (b.status === "completed") return;
      const slotMins = parseTimeToMins(b.time_slot);
      const windowEnd = currentTimeMins + 90;
      if (slotMins <= windowEnd) upNext.push(b);
      else later.push(b);
    });

    inService.sort(byTime);
    waiting.sort(byTime);
    upNext.sort(byTime);
    later.sort(byTime);
    const all = [...inService, ...waiting, ...upNext, ...later].sort(byTime);
    return { inService, waiting, upNext, later, all, searchMode: false };
  }, [bookings, deskSearch, deskSearchResults, bookingSessionState, deskIsToday, currentTimeMins]);

  const openDeskSheet = (b) => setDeskSheetBooking(b);
  const closeDeskSheet = () => setDeskSheetBooking(null);

  const quickActionProps = {
    lang,
    t,
    isBookingInvoiced,
    onOpenEdit: openEdit,
    onChangeStatus: changeStatus,
    onCheckIn: checkInCustomer,
    onStartService: startService,
    onDelete: quickDelete,
  };

  return (
    <div className="space-y-4" data-testid="bookings-page" data-owner-board={ownerBoard ? "true" : "false"}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl gold-text">{t("nav_bookings")}</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {bookings.length} {lang === "ar" ? "حجز" : "bookings"}
            {ownerBoard && <> · {staffList.length} {lang === "ar" ? "موظف" : "staff"}</>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {ownerBoard && branches && branches.length > 0 && (
            <div className="flex items-center gap-1.5 bg-[var(--surface)] border border-[var(--border)] rounded-md px-2 py-1" data-testid="bookings-branch-filter">
              <Building2 size={14} className="text-[var(--gold)]" />
              <select
                className="bg-transparent text-sm outline-none text-[var(--text)] pe-2"
                value={pageBranch}
                onChange={(e) => setPageBranch(e.target.value)}
                data-testid="bookings-branch-select"
              >
                {branches.map((b) => (
                  <option key={b.branch_id} value={b.branch_id}>{branchLabel(b)}{b.is_default ? " ★" : ""}</option>
                ))}
                {isSalonAdmin && branches.length > 1 && (
                  <option value={ALL_BRANCHES}>{lang === "ar" ? "كل الفروع" : "All branches"}</option>
                )}
              </select>
            </div>
          )}
          {ownerBoard && (
            <div className="flex items-center gap-1 bg-[var(--surface)] border border-[var(--border)] rounded-md p-0.5">
              <button onClick={() => setView("calendar")} data-testid="view-calendar" className={`p-1.5 rounded ${view === "calendar" ? "bg-[var(--gold)] text-white" : "text-[var(--text-muted)]"}`}><Grid3x3 size={16} /></button>
              <button onClick={() => setView("list")} data-testid="view-list" className={`p-1.5 rounded ${view === "list" ? "bg-[var(--gold)] text-white" : "text-[var(--text-muted)]"}`}><List size={16} /></button>
            </div>
          )}
          <div className="flex items-center gap-1 bg-[var(--surface)] border border-[var(--border)] rounded-md">
            <button onClick={() => shiftDate(-1)} data-testid="prev-day-btn" className="p-2 hover:bg-[var(--bg)] min-h-[44px] min-w-[44px] flex items-center justify-center"><ChevronRight size={16} className={lang === "ar" ? "" : "hidden"} /><ChevronLeft size={16} className={lang === "ar" ? "hidden" : ""} /></button>
            <input type="date" data-testid="date-picker" className="bg-transparent px-2 py-1.5 text-sm outline-none text-[var(--text)] min-h-[44px]" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
            <button onClick={() => shiftDate(1)} data-testid="next-day-btn" className="p-2 hover:bg-[var(--bg)] min-h-[44px] min-w-[44px] flex items-center justify-center"><ChevronLeft size={16} className={lang === "ar" ? "" : "hidden"} /><ChevronRight size={16} className={lang === "ar" ? "hidden" : ""} /></button>
          </div>
          <button onClick={() => setSelectedDate(todayISO())} data-testid="today-btn" className="px-3 py-2 border border-[var(--border)] rounded-md text-sm hover:border-[var(--gold)] min-h-[44px]">{lang === "ar" ? "اليوم" : "Today"}</button>
          <button onClick={openNewBooking} data-testid="new-booking-btn" className="btn-gold px-4 py-2.5 rounded-md flex items-center gap-2 text-sm min-h-[44px]"><Plus size={16} />{t("new_booking")}</button>
        </div>
      </div>

      {/* Past / overdue bookings — always visible so staff can follow up */}
      <div className="card-surface border border-amber-500/30 overflow-hidden" data-testid="overdue-bookings-panel">
        <button
          type="button"
          onClick={() => setOverdueExpanded((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-amber-500/10 hover:bg-amber-500/15 transition text-start"
          data-testid="overdue-bookings-toggle"
        >
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-amber-500/20 text-amber-600 flex items-center justify-center shrink-0 mt-0.5">
              <AlertTriangle size={18}/>
            </div>
            <div className="min-w-0">
              <div className="font-medium text-[var(--text)] flex items-center gap-2 flex-wrap">
                {t("booking_overdue_title")}
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-700 font-semibold num-display" data-testid="overdue-bookings-count">
                  {overdueBookings.length}
                </span>
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">{t("booking_overdue_help")}</p>
            </div>
          </div>
          <span className="text-xs text-[var(--text-muted)] shrink-0">{overdueExpanded ? (lang === "ar" ? "إخفاء" : "Hide") : (lang === "ar" ? "عرض" : "Show")}</span>
        </button>
        {overdueExpanded && (
          <div className="border-t border-amber-500/20">
            {overdueBookings.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-[var(--text-muted)]" data-testid="overdue-bookings-empty">
                {t("booking_overdue_empty")}
              </div>
            ) : (
              <div className="overflow-auto max-h-64">
                <table className="table-dark text-sm">
                  <thead>
                    <tr>
                      <th>{t("date")}</th>
                      <th>{t("time")}</th>
                      {showBranchColumn && <th>{lang === "ar" ? "الفرع" : "Branch"}</th>}
                      <th>{t("customer")}</th>
                      <th>{lang === "ar" ? "الموظف" : "Staff"}</th>
                      <th>{t("status")}</th>
                      <th className="text-end">{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overdueBookings.map((b) => (
                      <tr key={b.booking_id} data-testid={`overdue-row-${b.booking_id}`}>
                        <td>
                          <div className="font-medium num-display">{b.date}</div>
                          <div className="text-[10px] text-amber-600">{overdueDaysLabel(b.date)}</div>
                        </td>
                        <td className="num-display">{b.time_slot || "—"}</td>
                        {showBranchColumn && <td className="text-[var(--text-muted)]">{branchNameById[b.branch_id] || "—"}</td>}
                        <td>
                          <div className="font-medium">{displayCustomerName(b)}</div>
                          {b.customer_phone && <div className="text-[10px] text-[var(--text-muted)]" dir="ltr">{b.customer_phone}</div>}
                        </td>
                        <td className="text-[var(--text-muted)]">{staffNameById[b.employee_user_id] || "—"}</td>
                        <td>{statusBadge(effectiveBookingStatus(b))}</td>
                        <td className="text-end whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => openOverdueBooking(b)}
                            className="text-xs px-2.5 py-1 rounded-md border border-[var(--border)] hover:border-[var(--gold)] me-1"
                            data-testid={`overdue-open-${b.booking_id}`}
                          >
                            {t("booking_overdue_open")}
                          </button>
                          <button
                            type="button"
                            onClick={() => dismissOverdueBooking(b, "no_show")}
                            className="text-xs px-2.5 py-1 rounded-md border border-stone-300 text-stone-700 hover:bg-stone-50 me-1"
                            data-testid={`overdue-noshow-${b.booking_id}`}
                          >
                            {t("booking_no_show")}
                          </button>
                          <button
                            type="button"
                            onClick={() => dismissOverdueBooking(b, "cancelled")}
                            className="text-xs px-2.5 py-1 rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50 me-1"
                            data-testid={`overdue-cancel-${b.booking_id}`}
                          >
                            {t("cancel_booking")}
                          </button>
                          <button
                            type="button"
                            onClick={() => goToBookingDay(b.date)}
                            className="text-xs px-2.5 py-1 rounded-md border border-[var(--border)] hover:border-[var(--gold)]"
                            data-testid={`overdue-goto-${b.booking_id}`}
                          >
                            {t("booking_overdue_go_to_day")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Calendar View — owner only */}
      {ownerBoard && view === "calendar" && (
        <div className="bg-[var(--surface)] rounded-xl border border-[var(--border)] overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-220px)]">
            <div className="grid min-w-max" style={{ gridTemplateColumns: `70px repeat(${columns.length}, minmax(180px, 1fr))` }}>
              {/* Header row */}
              <div className="sticky top-0 z-20 bg-[var(--surface)] border-b border-[var(--border)] p-2"></div>
              {columns.map((col) => (
                <div key={col.id} className="sticky top-0 z-20 bg-[var(--surface)] border-b border-s border-[var(--border)] p-3 text-center">
                  <div className="w-10 h-10 mx-auto rounded-full bg-gradient-to-br from-[var(--gold)] to-[var(--rose)] text-white flex items-center justify-center font-bold text-sm shadow">
                    {col.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="text-sm font-semibold text-[var(--text)] mt-1 truncate">{col.name}</div>
                  {col.role && <div className="text-[10px] text-[var(--text-muted)]">{col.role}</div>}
                </div>
              ))}

              {/* Time labels column */}
              <div className="relative border-e border-[var(--border)]" style={{ height: `${gridHeight}px` }}>
                {hours.map((h, i) => (
                  <div key={h} className="absolute inset-x-0 text-[10px] text-[var(--text-muted)] font-mono text-center" style={{ top: `${i * ROW_HEIGHT - 6}px` }}>
                    {formatTime(h * 60)}
                  </div>
                ))}
              </div>

              {/* Employee columns */}
              {columns.map((col) => (
                <div key={col.id} className="relative border-s border-[var(--border)]" style={{ height: `${gridHeight}px` }} data-testid={`calendar-col-${col.id}`}>
                  {hours.map((h, i) => (
                    <div key={h} className={`absolute inset-x-0 border-t ${i % 2 === 0 ? "border-[var(--border)]" : "border-[var(--border)]/50 border-dashed"}`} style={{ top: `${i * ROW_HEIGHT}px`, height: `${ROW_HEIGHT}px` }} />
                  ))}
                  {(bookingsByEmployee[col.id] || []).map(renderBookingBlock)}
                </div>
              ))}
            </div>
          </div>
          {bookings.length === 0 && (
            <div className="text-center text-[var(--text-muted)] py-10 border-t border-[var(--border)]">
              {lang === "ar" ? "لا توجد حجوزات في هذا اليوم" : "No bookings for this day"}
            </div>
          )}
        </div>
      )}

      {/* List View — owner only */}
      {ownerBoard && view === "list" && (
        <div className="card-surface overflow-auto">
          <table className="table-dark">
            <thead><tr>
              <th>{t("time")}</th>
              {showBranchColumn && <th>{lang === "ar" ? "الفرع" : "Branch"}</th>}
              <th>{t("customer")}</th><th>{t("phone")}</th><th>{lang === "ar" ? "الخدمات" : "Items"}</th><th>{lang === "ar" ? "الموظف" : "Staff"}</th><th>{t("total")}</th><th>{t("status")}</th><th>{t("actions")}</th>
            </tr></thead>
            <tbody>
              {bookings.sort((a, b) => (a.time_slot || "").localeCompare(b.time_slot || "")).map((b) => {
                const emp = users.find((u) => u.user_id === b.employee_user_id);
                const preview = b.items?.length ? b.items.map((i) => (lang === "ar" ? i.name_ar : i.name_en)).join(", ") : (lang === "ar" ? b.item_name_ar : b.item_name_en);
                return (
                  <tr key={b.booking_id} className="cursor-pointer hover:bg-[var(--bg)]" onClick={() => openEdit(b)}>
                    <td className="font-mono text-[var(--gold)]">{b.time_slot}</td>
                    {showBranchColumn && (
                      <td className="text-sm">
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[var(--gold-tint)] text-[var(--gold)]">
                          <Building2 size={10} />{branchNameById[b.branch_id] || "—"}
                        </span>
                      </td>
                    )}
                    <td>{displayCustomerName(b) || (lang === "ar" ? "—" : "—")}</td>
                    <td dir="ltr">{b.customer_phone}</td>
                    <td className="text-sm">{preview}</td>
                    <td className="text-sm">{emp ? (emp.name || emp.username) : <span className="text-[var(--text-muted)]">—</span>}</td>
                    <td className="gold-text font-semibold">{b.total_price ?? b.price ?? 0}</td>
                    <td>{statusBadge(effectiveBookingStatus(b))}</td>
                    <td><button onClick={(e) => { e.stopPropagation(); openEdit(b); }} className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--gold)] text-xs">{lang === "ar" ? "تعديل" : "Edit"}</button></td>
                  </tr>
                );
              })}
              {bookings.length === 0 && <tr><td colSpan={showBranchColumn ? 9 : 8} className="text-center text-[var(--text-muted)] py-8">{t("no_data")}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Desk view — operational roles */}
      {!ownerBoard && (
        <BookingsDeskView
          lang={lang}
          selectedDate={selectedDate}
          isToday={deskIsToday}
          deskSearch={deskSearch}
          setDeskSearch={setDeskSearch}
          groups={deskGroups}
          laterExpanded={laterTodayExpanded}
          setLaterExpanded={setLaterTodayExpanded}
          displayCustomerName={displayCustomerName}
          staffNameById={staffNameById}
          statusBadge={statusBadge}
          effectiveBookingStatus={effectiveBookingStatus}
          bookingSessionState={bookingSessionState}
          liveSessionMinutes={liveSessionMinutes}
          visitTick={visitTick}
          t={t}
          onOpenBooking={openDeskSheet}
          searchMode={deskGroups.searchMode}
          searchLoading={deskSearchLoading}
        />
      )}

      {/* Desk action sheet (touch) */}
      {deskSheetBooking && (
        <div className="fixed inset-0 z-[60] flex flex-col justify-end" data-testid="desk-action-sheet">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close"
            onClick={closeDeskSheet}
          />
          <div className="relative bg-[var(--surface)] rounded-t-2xl border-t border-[var(--border)] shadow-2xl max-h-[70vh] overflow-auto pb-safe">
            <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-bold text-lg truncate">{displayCustomerName(deskSheetBooking) || (lang === "ar" ? "حجز" : "Booking")}</div>
                <div className="text-sm text-[var(--text-muted)] font-mono">{deskSheetBooking.time_slot} · {deskSheetBooking.date}</div>
              </div>
              <button type="button" onClick={closeDeskSheet} className="p-2 rounded-full hover:bg-[var(--bg)] min-h-[44px] min-w-[44px] flex items-center justify-center">
                <X size={20} />
              </button>
            </div>
            <BookingQuickActions
              b={deskSheetBooking}
              {...quickActionProps}
              onClose={closeDeskSheet}
              className="py-2"
            />
          </div>
        </div>
      )}

      {/* NEW BOOKING MODAL */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="card-surface p-6 w-full max-w-3xl space-y-4 max-h-[92vh] overflow-auto" onMouseDown={onModalPanelMouseDown} data-testid="new-booking-modal">
            <h2 className="font-heading text-2xl gold-text flex items-center gap-2"><CalendarIcon size={22} />{t("new_booking")}</h2>

            {/* Branch (always visible; if there are 2+ branches we require an explicit choice) */}
            {branches && branches.length > 0 && (
              <div>
                <label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Building2 size={12} />{lang === "ar" ? "الفرع" : "Branch"}</label>
                <select
                  className="input-dark"
                  value={form.branch_id}
                  onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                  data-testid="booking-branch-select"
                >
                  <option value="">{lang === "ar" ? "اختر الفرع..." : "Select branch..."}</option>
                  {branches.map((b) => (
                    <option key={b.branch_id} value={b.branch_id}>{branchLabel(b)}{b.is_default ? " ★" : ""}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-[var(--text-muted)]">{t("customer")}</label>
                  <button
                    type="button"
                    onClick={() => setNewCustOpen((v) => !v)}
                    className="text-[11px] text-[var(--gold)] hover:underline flex items-center gap-1"
                    data-testid="toggle-new-customer-btn"
                  >
                    <UserPlus size={12}/>{newCustOpen ? (lang === "ar" ? "إلغاء" : "Cancel") : (lang === "ar" ? "+ عميل جديد" : "+ New customer")}
                  </button>
                </div>
                <div className="relative mt-1" ref={custBoxRef}>
                  <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-[var(--text-muted)] pointer-events-none" />
                  <input
                    type="text"
                    className="input-dark ps-9 pe-9"
                    placeholder={lang === "ar" ? "ابحث بالاسم أو الهاتف..." : "Search by name or phone..."}
                    value={custQuery}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCustQuery(value);
                      setCustDropOpen(true);
                      if (!value.trim()) clearBookingCustomer();
                    }}
                    onFocus={() => setCustDropOpen(true)}
                    data-testid="booking-customer-select"
                  />
                  {(form.customer_id || custQuery) && (
                    <button
                      type="button"
                      onClick={clearBookingCustomer}
                      className="absolute top-1/2 -translate-y-1/2 end-2 p-1 text-[var(--text-muted)] hover:text-rose-500"
                      data-testid="booking-customer-clear"
                    >
                      <X size={14} />
                    </button>
                  )}
                  {custDropOpen && (
                    <div className="absolute z-30 mt-1 left-0 right-0 max-h-60 overflow-auto rounded-md border border-[var(--border)] bg-[var(--surface)] shadow-xl" data-testid="booking-customer-dropdown">
                      <button
                        type="button"
                        onClick={() => selectBookingCustomer(null)}
                        className="w-full text-start px-3 py-2 text-sm text-[var(--text-muted)] hover:bg-[var(--surface-2)] border-b border-[var(--border-subtle)]"
                        data-testid="booking-customer-walkin"
                      >
                        {t("walk_in")}
                      </button>
                      {filteredCustomers.length === 0 ? (
                        <div className="px-3 py-3 text-sm text-[var(--text-muted)]">
                          {lang === "ar" ? "لا توجد نتائج" : "No matches"}
                        </div>
                      ) : filteredCustomers.map((c) => (
                        <button
                          key={c.customer_id}
                          type="button"
                          onClick={() => selectBookingCustomer(c)}
                          className={`w-full text-start px-3 py-2 text-sm hover:bg-[var(--surface-2)] flex items-center justify-between gap-3 ${form.customer_id === c.customer_id ? "bg-[var(--surface-2)] text-[var(--gold)]" : "text-[var(--text)]"}`}
                          data-testid={`booking-customer-opt-${c.customer_id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{c.name}</div>
                            {c.phone && <div className="text-xs text-[var(--text-muted)]" dir="ltr">{c.phone}</div>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("name")}</label><input className="input-dark" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value, customer_id: "" })} data-testid="booking-customer-name" /></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("phone")}</label><input className="input-dark" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value, customer_id: "" })} data-testid="booking-customer-phone" /></div>
              <div><label className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "الموظف" : "Staff"}</label>
                <select className="input-dark" value={form.employee_user_id} onChange={(e) => setForm({ ...form, employee_user_id: e.target.value })} data-testid="booking-employee-select">
                  <option value="">{lang === "ar" ? "غير مُسند" : "Unassigned"}</option>
                  {modalStaff.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.username}</option>)}
                </select>
                {form.branch_id && modalStaff.length === 0 && (
                  <div className="text-[11px] text-amber-600 mt-1">{lang === "ar" ? "لا يوجد موظفون مسجلون في هذا الفرع" : "No staff assigned to this branch"}</div>
                )}
                {rotaWarningText(rotaCheck, t, lang) && (
                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 mt-1 flex items-start gap-1" data-testid="booking-rota-warning">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0"/>
                    <span>{rotaWarningText(rotaCheck, t, lang)}</span>
                  </div>
                )}
              </div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("date")}</label><input type="date" className="input-dark" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            </div>

            {/* Inline "Create new customer" panel */}
            {newCustOpen && (
              <div className="bg-[var(--gold-tint)] border border-[var(--gold)]/30 rounded-md p-3 space-y-2" data-testid="new-customer-panel">
                <div className="text-xs font-semibold text-[var(--gold)] flex items-center gap-1"><UserPlus size={12}/>{lang === "ar" ? "إضافة عميل جديد" : "Add new customer"}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div><label className="text-[11px] text-[var(--text-muted)] flex items-center gap-1"><User size={10}/>{lang === "ar" ? "الاسم *" : "Name *"}</label><input className="input-dark" value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} data-testid="new-customer-name" /></div>
                  <div><label className="text-[11px] text-[var(--text-muted)] flex items-center gap-1"><Phone size={10}/>{lang === "ar" ? "الهاتف *" : "Phone *"}</label><input className="input-dark" value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} data-testid="new-customer-phone" /></div>
                  <div><label className="text-[11px] text-[var(--text-muted)] flex items-center gap-1"><Mail size={10}/>{lang === "ar" ? "البريد (اختياري)" : "Email (optional)"}</label><input className="input-dark" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} data-testid="new-customer-email" /></div>
                  <div>
                    <label className="text-[11px] text-[var(--text-muted)]">{lang === "ar" ? "اللغة المفضّلة" : "Language"}</label>
                    <select className="input-dark" value={newCust.language_pref} onChange={(e) => setNewCust({ ...newCust, language_pref: e.target.value })}>
                      <option value="ar">العربية</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                  <div className="md:col-span-2"><label className="text-[11px] text-[var(--text-muted)]">{t("notes")}</label><textarea className="input-dark" rows={2} value={newCust.notes} onChange={(e) => setNewCust({ ...newCust, notes: e.target.value })} /></div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={() => { setNewCustOpen(false); setNewCust({ name: "", phone: "", email: "", language_pref: "ar", notes: "" }); }} className="px-3 py-1.5 rounded border border-[var(--border)] text-xs">{t("cancel")}</button>
                  <button type="button" onClick={saveNewCustomer} disabled={savingCust} className="btn-gold px-4 py-1.5 rounded text-xs flex items-center gap-1" data-testid="save-new-customer-btn">
                    <UserPlus size={12}/>{savingCust ? (lang === "ar" ? "جارٍ الحفظ..." : "Saving...") : (lang === "ar" ? "حفظ العميل" : "Save customer")}
                  </button>
                </div>
              </div>
            )}

            <BookingItemsEditor
              items={items}
              services={services}
              packages={packages}
              staffList={modalStaff}
              disabled={false}
              lang={lang}
              t={t}
              onAddService={addServiceItem}
              onAddPackage={addPackageItem}
              onEditPackage={editPackageItem}
              onQtyChange={updateItemQty}
              onRemove={removeItem}
              onStaffChange={setItemStaff}
            />

            <div>
              <label className="text-xs text-[var(--text-muted)]">{t("time")}</label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {(slotCap.length ? slotCap : DEFAULT_SLOTS.map((ts) => ({ time_slot: ts, remaining: 2, capacity: 2 }))).map((s) => (
                  <button key={s.time_slot} type="button" disabled={s.remaining <= 0} onClick={() => setForm({ ...form, time_slot: s.time_slot })} className={`p-2 rounded text-xs border transition ${form.time_slot === s.time_slot ? "btn-gold" : "border-[var(--border)] hover:border-[var(--gold)] text-[var(--text)]"} ${s.remaining <= 0 ? "opacity-40 cursor-not-allowed" : ""}`}>
                    <div>{s.time_slot}</div>
                    <div className="text-[10px] opacity-70">{s.remaining <= 0 ? t("full") : `${s.remaining}/${s.capacity}`}</div>
                  </button>
                ))}
              </div>
            </div>
            <div><label className="text-xs text-[var(--text-muted)]">{t("notes")}</label><textarea className="input-dark" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="flex gap-3 justify-end">
              <button className="px-4 py-2 rounded-md border border-[var(--border)]" onClick={() => setOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" data-testid="booking-save-btn" onClick={submit}>{t("book")}</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT BOOKING MODAL */}
      {editBooking && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setEditBooking(null))}>
          <div className="card-surface p-6 w-full max-w-lg space-y-4 max-h-[92vh] overflow-auto" onMouseDown={onModalPanelMouseDown} data-testid="edit-booking-modal">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-heading text-2xl gold-text">{lang === "ar" ? "تفاصيل الحجز" : "Booking Details"}</h2>
                <p className="text-xs text-[var(--text-muted)] font-mono mt-1">{editBooking.booking_id}</p>
                {editBooking.branch_id && (
                  <p className="text-xs text-[var(--gold)] mt-1 flex items-center gap-1"><Building2 size={10}/>{branchNameById[editBooking.branch_id] || editBooking.branch_id}</p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                {statusBadge(effectiveBookingStatus(editBooking))}
                {isBookingInvoiced(editBooking) && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold flex items-center gap-1" data-testid="booking-invoiced-badge">
                    <Receipt size={10}/>{t("booking_invoiced")}
                  </span>
                )}
              </div>
            </div>

            {!isBookingInvoiced(editBooking) && isBookingStale(editBooking, todayISO()) && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="booking-stale-banner">
                <div className="font-semibold flex items-center gap-2">
                  <AlertTriangle size={16}/>
                  {t("booking_stale_banner")}
                </div>
              </div>
            )}

            {isBookingInvoiced(editBooking) && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="booking-invoiced-banner">
                <div className="font-semibold flex items-center gap-2">
                  <Receipt size={16}/>
                  {t("booking_invoiced")}
                  {editBooking.invoice_number && (
                    <span className="font-mono text-emerald-900">#{editBooking.invoice_number}</span>
                  )}
                </div>
                <p className="text-xs mt-1 text-emerald-700">
                  {editItems.some((it) => (it.selected_services || []).some((s) => s.status === "pending"))
                    ? (lang === "ar"
                      ? "يمكنك استبدال الخدمات المعلقة فقط في باقات اختر N."
                      : "You can swap pending services only on pick-n packages.")
                    : t("booking_invoiced_locked")}
                </p>
              </div>
            )}

            {(editBooking.arrived_at || editBooking.service_started_at) && !isBookingInvoiced(editBooking) && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2">
                {editBooking.service_started_at
                  ? (lang === "ar" ? "الخدمة جارية — يمكنك إضافة خدمات أو باقات وتعيين الموظفين." : "Service in progress — you can add services or packages and assign staff.")
                  : (lang === "ar" ? "العميل وصل — يمكنك تعديل العناصر قبل أو أثناء الخدمة." : "Customer arrived — you can edit items before or during service.")}
              </div>
            )}

            <BookingItemsEditor
              items={editItems}
              services={services}
              packages={packages}
              staffList={editModalStaff.length ? editModalStaff : staffList.filter((u) => userInBranch(u, editBooking.branch_id))}
              disabled={isBookingInvoiced(editBooking)}
              invoicedReadOnly={isBookingInvoiced(editBooking)}
              lang={lang}
              t={t}
              onAddService={addEditServiceItem}
              onAddPackage={addEditPackageItem}
              onEditPackage={editEditPackageItem}
              onSwapPending={requestPendingSwap}
              onQtyChange={(idx, d) => updateListItemQty(setEditItems, idx, d)}
              onRemove={(idx) => removeListItem(setEditItems, idx)}
              onStaffChange={(idx, userId) => setListItemStaff(setEditItems, idx, userId)}
            />

            {/* Editable fields — locked once invoiced */}
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${isBookingInvoiced(editBooking) ? "opacity-80 pointer-events-none" : ""}`}>
              <div><label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><User size={12} />{t("name")}</label><input data-testid="edit-name" className="input-dark" value={editForm.customer_name} onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })} readOnly={isBookingInvoiced(editBooking)} /></div>
              <div><label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Phone size={12} />{t("phone")}</label><input data-testid="edit-phone" className="input-dark" value={editForm.customer_phone} onChange={(e) => setEditForm({ ...editForm, customer_phone: e.target.value })} readOnly={isBookingInvoiced(editBooking)} /></div>
              <div><label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><CalendarIcon size={12} />{t("date")}</label><input type="date" className="input-dark" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} readOnly={isBookingInvoiced(editBooking)} /></div>
              <div><label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Clock size={12} />{t("time")}</label><input type="time" className="input-dark" value={editForm.time_slot} onChange={(e) => setEditForm({ ...editForm, time_slot: e.target.value })} readOnly={isBookingInvoiced(editBooking)} /></div>
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><UserCheck size={12} />{lang === "ar" ? "الموظف المسؤول" : "Assigned Staff"}</label>
                <select data-testid="edit-employee" className="input-dark" value={editForm.employee_user_id} onChange={(e) => setEditForm({ ...editForm, employee_user_id: e.target.value })} disabled={isBookingInvoiced(editBooking)}>
                  <option value="">{lang === "ar" ? "غير مُسند" : "Unassigned"}</option>
                  {staffList.filter((u) => userInBranch(u, editBooking.branch_id)).map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.username} ({u.role})</option>)}
                </select>
                {rotaWarningText(editRotaCheck, t, lang) && (
                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 mt-1 flex items-start gap-1" data-testid="edit-booking-rota-warning">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0"/>
                    <span>{rotaWarningText(editRotaCheck, t, lang)}</span>
                  </div>
                )}
              </div>
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><StickyNote size={12} />{t("notes")}</label><textarea data-testid="edit-notes" className="input-dark" rows={2} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} readOnly={isBookingInvoiced(editBooking)} /></div>
            </div>

            {/* Visit duration — always show for invoiced bookings */}
            {(isBookingInvoiced(editBooking) || editBooking.arrived_at || editBooking.visit_duration_minutes != null) && (
              <div className="rounded-md border border-[var(--border-subtle)] bg-[var(--bg-alt)] p-3 space-y-1 text-sm" data-testid="visit-duration-panel">
                <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">{t("visit_duration")}</div>
                {editBooking.arrived_at && (
                  <div className="flex justify-between gap-2">
                    <span className="text-[var(--text-muted)]">{t("customer_arrived")}</span>
                    <span className="num-display">{formatTs(editBooking.arrived_at)}</span>
                  </div>
                )}
                {editBooking.service_started_at && (
                  <div className="flex justify-between gap-2">
                    <span className="text-[var(--text-muted)]">{t("service_started")}</span>
                    <span className="num-display">{formatTs(editBooking.service_started_at)}</span>
                  </div>
                )}
                {(editBooking.paid_at || editBooking.invoiced_at) && (
                  <div className="flex justify-between gap-2">
                    <span className="text-[var(--text-muted)]">{lang === "ar" ? "وقت الدفع" : "Paid at"}</span>
                    <span className="num-display">{formatTs(editBooking.paid_at || editBooking.invoiced_at)}</span>
                  </div>
                )}
                {(() => {
                  const live = liveSessionMinutes(editBooking);
                  void visitTick;
                  if (live.waiting != null && (editBooking.service_started_at || isBookingInvoiced(editBooking))) {
                    return (
                      <div className="flex justify-between gap-2">
                        <span className="text-[var(--text-muted)]">{t("waiting_time")}</span>
                        <span className="num-display">{live.waiting} {lang === "ar" ? "د" : "min"}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
                {(() => {
                  const live = liveSessionMinutes(editBooking);
                  void visitTick;
                  if (live.service != null && editBooking.service_started_at) {
                    return (
                      <div className="flex justify-between gap-2">
                        <span className="text-[var(--text-muted)]">{t("service_duration")}</span>
                        <span className="num-display">{live.service} {lang === "ar" ? "د" : "min"}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
                {editBooking.visit_duration_minutes != null && (
                  <div className="flex justify-between gap-2 font-semibold text-base pt-1 border-t border-[var(--border-subtle)]">
                    <span className="text-[var(--text-muted)]">{t("time_spent")}</span>
                    <span className="gold-text num-display">{editBooking.visit_duration_minutes} {lang === "ar" ? "دقيقة" : "min"}</span>
                  </div>
                )}
                {isBookingInvoiced(editBooking) && editBooking.visit_duration_minutes == null && (
                  <div className="text-xs text-rose-600 pt-1">
                    {editBooking.arrived_at ? t("visit_duration_unavailable") : t("missing_arrival")}
                  </div>
                )}
                {editBooking.arrived_at && !isBookingInvoiced(editBooking) && (() => {
                  const live = liveSessionMinutes(editBooking);
                  const st = bookingSessionState(editBooking);
                  const stale = st === "stale" || live.stale;
                  void visitTick;
                  return (
                    <div className="flex justify-between gap-2 text-amber-700 pt-1 border-t border-[var(--border-subtle)]" data-testid="visit-in-progress">
                      <span>{stale ? t("booking_stale_timer_note") : (st === "waiting" ? t("waiting") : st === "in_service" ? t("in_service") : t("in_progress"))}</span>
                      <span className="num-display">
                        {live.total ?? 0} {lang === "ar" ? "د" : "min"}
                        {!stale && live.waiting != null && live.service != null && (
                          <span className="text-[10px] text-[var(--text-muted)] ms-1">
                            ({live.waiting}+{live.service})
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Status Actions — hidden when invoiced */}
            {!isBookingInvoiced(editBooking) && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {!editBooking.arrived_at && editBooking.status !== "cancelled" && (
                <button
                  data-testid="mark-checkin"
                  onClick={() => checkInCustomer()}
                  className="flex items-center justify-center gap-2 p-3 rounded-md bg-amber-50 text-amber-800 hover:bg-amber-100 col-span-2 sm:col-span-4"
                >
                  <LogIn size={16} /> {t("customer_arrived")}
                </button>
              )}
              {editBooking.arrived_at && !editBooking.service_started_at && editBooking.status !== "cancelled" && (
                <button
                  data-testid="mark-start-service"
                  onClick={() => startService()}
                  className="flex items-center justify-center gap-2 p-3 rounded-md bg-violet-50 text-violet-800 hover:bg-violet-100 col-span-2 sm:col-span-4"
                >
                  <Scissors size={16} /> {t("service_started")}
                </button>
              )}
              <button data-testid="mark-completed" onClick={() => changeStatus("completed")} disabled={editBooking.status === "completed"} className="flex items-center justify-center gap-2 p-3 rounded-md bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <CheckCircle2 size={16} /> {lang === "ar" ? "مكتمل" : "Complete"}
              </button>
              <button data-testid="mark-no-show" onClick={() => changeStatus("no_show")} disabled={editBooking.status === "no_show"} className="flex items-center justify-center gap-2 p-3 rounded-md bg-stone-100 text-stone-700 hover:bg-stone-200 disabled:opacity-50 disabled:cursor-not-allowed">
                <User size={16} /> {t("booking_no_show")}
              </button>
              <button data-testid="mark-confirmed" onClick={() => changeStatus("confirmed")} disabled={editBooking.status === "confirmed"} className="flex items-center justify-center gap-2 p-3 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <UserCheck size={16} /> {lang === "ar" ? "مؤكد" : "Confirm"}
              </button>
              <button data-testid="mark-cancelled" onClick={() => changeStatus("cancelled")} disabled={editBooking.status === "cancelled"} className="flex items-center justify-center gap-2 p-3 rounded-md bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <XCircle size={16} /> {lang === "ar" ? "ملغي" : "Cancel"}
              </button>
            </div>
            )}

            {/* Footer actions */}
            <div className="flex gap-2 justify-between pt-3 border-t border-[var(--border)]">
              {!isBookingInvoiced(editBooking) ? (
                <button onClick={deleteBooking} className="px-4 py-2 rounded-md border border-rose-300 text-rose-600 hover:bg-rose-50 text-sm flex items-center gap-2" data-testid="delete-booking"><Trash2 size={14} /> {lang === "ar" ? "حذف" : "Delete"}</button>
              ) : (
                <div />
              )}
              <div className="flex gap-2 ms-auto">
                <button onClick={() => setEditBooking(null)} className="px-4 py-2 rounded-md border border-[var(--border)]">{isBookingInvoiced(editBooking) ? (lang === "ar" ? "إغلاق" : "Close") : t("cancel")}</button>
                {!isBookingInvoiced(editBooking) && (
                  <button onClick={saveEdit} data-testid="save-edit-btn" className="btn-gold px-6 py-2 rounded-md">{lang === "ar" ? "حفظ" : "Save"}</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <PickPackageModal
        open={!!pickPackageModal}
        pkg={pickPackageModal?.pkg}
        services={services}
        staff={pickPackageModal?.staff || []}
        lang={lang}
        requireStaff
        initialSelections={pickPackageModal?.initialSelections}
        confirmLabel={pickPackageModal?.confirmLabel}
        pendingSwapMode={pickPackageModal?.pendingSwapMode}
        onClose={() => setPickPackageModal(null)}
        onConfirm={(selections) => pickPackageModal?.onConfirm?.(selections)}
      />
      <ConfirmDialogSlot />
    </div>
  );
}
